/**
 * The checkpoint log as a graph: which lane each node sits in, and where the
 * lines between them run.
 *
 * Modelled on VS Code's Git Graph. Lanes are fixed-width columns; a node
 * continues its parent's lane when it is that parent's first child, and opens a
 * new lane otherwise, which is what a branch looks like. There is no merging in
 * this system, so every node has at most one parent and the hard half of a
 * commit-graph layout does not arise.
 *
 * Rows run oldest-first, the direction the transcript beside the rail grows.
 * Git Graph runs the other way; matching it would mean the two columns
 * disagreed about which end is "now".
 *
 * Pure. No React, no Automerge: it takes the refs list and HEAD, and returns
 * where to draw. Everything it needs to be robust against is in the data it is
 * handed, so all of it is testable without a DOM.
 */
import type { CheckpointRef } from "playground/project";

/** One restore point, as the rail draws it. */
export interface RailCheckpoint {
  id: string;
  /** The checkpoint's own label. The rail ellipsises; the tooltip shows it whole. */
  description: string;
  /** Directory it scopes. `"/"` is the whole project. */
  path: string;
  createdAt: number;
  /** The checkpoint this one branched from. Absent on a root. */
  parent?: string;
}

/** One drawn row: a node, its lane, and the lines crossing its band. */
export interface GraphRow {
  checkpoint: RailCheckpoint;
  /** Column this node's dot sits in. */
  lane: number;
  /** Other lanes whose line runs the full height of this row. */
  through: number[];
  /** This node's lane carries a line down into its dot. True whenever it has a
   *  parent, whether it continued that parent's lane or was branched into one. */
  hasLineAbove: boolean;
  /** Something below still hangs off this node's lane. */
  hasLineBelow: boolean;
  /**
   * Lanes this node spawns: one per child beyond the first, drawn as a curve
   * leaving THIS node's dot. Drawn at the child instead, a branch off an old
   * checkpoint reads as a branch off whatever sits directly above the child.
   */
  branchOut: number[];
  isActive: boolean;
  /** On the active node's own line of descent, root to HEAD. The collapsed rail
   *  shows only these: 36px is an index of where you are, not of everywhere the
   *  project has been. */
  onActiveBranch: boolean;
}

export interface CheckpointGraph {
  rows: GraphRow[];
  laneCount: number;
}

/**
 * Order the log for display: oldest first, ties broken by id.
 *
 * `createdAt` is stamped strictly increasing per peer (`checkpoints.ts`), so a
 * child always sorts below its parent and the lane walk below can assume it.
 * Two peers appending while disconnected can still collide on a timestamp,
 * which is what the id tiebreak is for: without it the two rows could swap
 * between renders and the lanes would appear to jitter.
 */
function displayOrder(refs: readonly CheckpointRef[]): CheckpointRef[] {
  return [...refs].sort(
    (a, b) => a.createdAt - b.createdAt || (a.id < b.id ? -1 : 1),
  );
}

function toRailCheckpoint(ref: CheckpointRef): RailCheckpoint {
  return {
    id: ref.id,
    description: ref.description,
    path: ref.path,
    createdAt: ref.createdAt,
    ...(ref.parent !== undefined ? { parent: ref.parent } : {}),
  };
}

/**
 * Lay the log out as lanes.
 *
 * Two passes. The first fixes row order and collects each node's children. The
 * second walks rows, and every node hands out the lanes its own children will
 * sit in: the first child inherits the node's lane, and each further child gets
 * a fresh one reserved from THIS row down to that child's.
 *
 * Reserving from the parent is what makes ancestry readable. A lane claimed at
 * the child's row instead leaves the parent's own lane running unbroken past
 * every row in between, so a branch off an old checkpoint looks like a branch
 * off whichever node happens to sit just above the child.
 *
 * `heldUntil[lane]` is therefore the row a lane's current line ends on, and a
 * lane is free again for anything strictly after it.
 *
 * Every checkpoint gets a row whatever directory it scopes. Agents are told to
 * checkpoint as deep in the tree as they can, so filtering to root-scoped ones
 * would empty the rail exactly when the agent is doing its job.
 */
export function railGraph(
  refs: readonly CheckpointRef[] | undefined,
  head: string | undefined,
): CheckpointGraph {
  const ordered = displayOrder(refs ?? []);
  const rowOf = new Map(ordered.map((ref, i) => [ref.id, i]));

  // A parent id nobody has is a root: two peers deleting neighbouring nodes
  // merge into exactly that, and dropping those rows would lose a subtree. A
  // parent that sorts BELOW its child reads as a root too, which can only come
  // from data written before timestamps were stamped monotonically.
  const parentRow = (ref: CheckpointRef, row: number): number | undefined => {
    if (ref.parent === undefined) return undefined;
    const at = rowOf.get(ref.parent);
    return at === undefined || at >= row ? undefined : at;
  };

  const children: number[][] = ordered.map(() => []);
  ordered.forEach((ref, i) => {
    const parent = parentRow(ref, i);
    if (parent !== undefined) children[parent]?.push(i);
  });

  const heldUntil: number[] = [];
  const laneOf: (number | undefined)[] = ordered.map(() => undefined);
  const rows: GraphRow[] = [];

  /** The leftmost lane whose line has ended before `row`. */
  const claimFree = (row: number): number => {
    const free = heldUntil.findIndex((until) => until < row);
    return free === -1 ? heldUntil.length : free;
  };
  const hold = (lane: number, until: number) => {
    heldUntil[lane] = Math.max(heldUntil[lane] ?? until, until);
  };

  ordered.forEach((ref, i) => {
    // Assigned already if this node's parent reserved it; a root claims now.
    const lane = laneOf[i] ?? claimFree(i);
    laneOf[i] = lane;
    hold(lane, i);

    const branchOut: number[] = [];
    children[i]?.forEach((childRow, nth) => {
      if (nth === 0) {
        laneOf[childRow] = lane; // the eldest carries this node's line on
        hold(lane, childRow);
        return;
      }
      const opened = claimFree(i);
      laneOf[childRow] = opened;
      hold(opened, childRow);
      branchOut.push(opened);
    });

    rows.push({
      checkpoint: toRailCheckpoint(ref),
      lane,
      // A lane opened on this row starts at the dot, so its curve draws it and
      // a full-height vertical would double back over the node.
      through: heldUntil.flatMap((until, l) =>
        l !== lane && until > i && !branchOut.includes(l) ? [l] : [],
      ),
      hasLineAbove: parentRow(ref, i) !== undefined,
      hasLineBelow: (heldUntil[lane] ?? i) > i,
      branchOut,
      isActive: ref.id === head,
      onActiveBranch: false, // filled in below, once HEAD's row is known
    });
  });

  // Walk HEAD back to its root, marking the line of descent. Same termination
  // rules as `parentRow`, so a dangling or out-of-order parent ends the walk.
  for (let at = ordered.findIndex((r) => r.id === head); at >= 0;) {
    const row = rows[at];
    const ref = ordered[at];
    if (!row || !ref) break;
    row.onActiveBranch = true;
    at = parentRow(ref, at) ?? -1;
  }

  return { rows, laneCount: heldUntil.length };
}
